import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '../services/api';
import { Building2, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';

const initialForm = {
  // Admin account
  name: '', email: '', password: '', confirmPassword: '', phone: '',
  // Company
  company_name: '', legal_name: '', gstin: '', pan: '', registration_no: '',
  address: '', city: '', state: '', pincode: '', country: 'India',
  company_phone: '', company_email: '', website: '',
  financial_year_start: '04-01', currency: 'INR',
};

export default function Register() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company_name.trim()) return toast.error('Company name is required');
    if (form.password !== form.confirmPassword) return toast.error('Passwords do not match');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');

    setLoading(true);
    try {
      const payload = { ...form };
      delete payload.confirmPassword;
      const { data } = await api.post('/auth/register', payload);
      if (data.success) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        toast.success(data.message || 'Registered successfully');
        setTimeout(() => nav('/'), 800);
      } else {
        toast.error(data.message || 'Registration failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrap">
      <form className="login-box" onSubmit={submit} style={{ maxWidth: 640 }}>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{ width: 60, height: 60, background: '#059669', borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
            <UserPlus size={30} />
          </div>
        </div>
        <h1 style={{ color: '#059669' }}>Create Admin Account</h1>
        <p className="subtitle">Register your organization. Staff accounts can be added later from the Users page.</p>

        {/* ===== Admin section ===== */}
        <h3 style={{ margin: '18px 0 8px', fontSize: 13, color: '#475569', letterSpacing: 0.5, textTransform: 'uppercase' }}>
          Your Account
        </h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Full Name *</label>
            <input value={form.name} onChange={set('name')} required autoFocus />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input type="email" value={form.email} onChange={set('email')} required />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="tel" value={form.phone} onChange={set('phone')} />
          </div>
          <div className="form-group">{/* spacer */}</div>
          <div className="form-group">
            <label>Password * (min 6 chars)</label>
            <input type="password" value={form.password} onChange={set('password')} required minLength={6} />
          </div>
          <div className="form-group">
            <label>Confirm Password *</label>
            <input type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required />
          </div>
        </div>

        {/* ===== Company section ===== */}
        <h3 style={{ margin: '18px 0 8px', fontSize: 13, color: '#475569', letterSpacing: 0.5, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Building2 size={14} /> Company Details
        </h3>
        <p style={{ fontSize: 12, color: '#64748b', marginTop: -4, marginBottom: 8 }}>
          Only Company Name is required now — you can fill in the rest later from the Company page.
        </p>
        <div className="form-group">
          <label>Company Name *</label>
          <input value={form.company_name} onChange={set('company_name')} required placeholder="e.g. Alpha Traders Pvt Ltd" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label>Legal Name</label>
            <input value={form.legal_name} onChange={set('legal_name')} />
          </div>
          <div className="form-group">
            <label>Registration No.</label>
            <input value={form.registration_no} onChange={set('registration_no')} />
          </div>
          <div className="form-group">
            <label>GSTIN</label>
            <input value={form.gstin} onChange={set('gstin')} placeholder="36AAAAA0000A1Z5" />
          </div>
          <div className="form-group">
            <label>PAN</label>
            <input value={form.pan} onChange={set('pan')} />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Address</label>
            <input value={form.address} onChange={set('address')} placeholder="Street, building, area" />
          </div>
          <div className="form-group">
            <label>City</label>
            <input value={form.city} onChange={set('city')} />
          </div>
          <div className="form-group">
            <label>State</label>
            <input value={form.state} onChange={set('state')} />
          </div>
          <div className="form-group">
            <label>Pincode</label>
            <input value={form.pincode} onChange={set('pincode')} />
          </div>
          <div className="form-group">
            <label>Country</label>
            <input value={form.country} onChange={set('country')} />
          </div>
          <div className="form-group">
            <label>Company Phone</label>
            <input value={form.company_phone} onChange={set('company_phone')} placeholder="Defaults to your phone" />
          </div>
          <div className="form-group">
            <label>Company Email</label>
            <input type="email" value={form.company_email} onChange={set('company_email')} placeholder="Defaults to your email" />
          </div>
          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
            <label>Website</label>
            <input value={form.website} onChange={set('website')} placeholder="https://example.com" />
          </div>
        </div>

        <button type="submit" className="btn-primary" style={{ background: '#059669', marginTop: 10 }} disabled={loading}>
          {loading ? 'Creating account...' : 'Create Admin Account'}
        </button>

        <p style={{ textAlign: 'center', marginTop: 18, fontSize: 12.5, color: '#64748b' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#1e40af', fontWeight: 600, textDecoration: 'none' }}>
            Sign in
          </Link>
        </p>
        <p style={{ textAlign: 'center', marginTop: 12, fontSize: 11, color: '#94a3b8' }}>© 2026 Noris Solutions</p>
      </form>
    </div>
  );
}
